import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { PeriodSelector } from '../PeriodSelector/PeriodSelector';
import type { PeriodValue } from '../../lib/period';
import {
  raEyebrow,
  raPerHundredAppointments,
  raWeeklyAxisMax,
} from '../../reminders-agent/derive';
import type {
  RaAgentData,
  RaChannel,
  RaEvent,
  RaEventTone,
  RaStep,
  RaWeek,
} from '../../reminders-agent/types';

export interface RemindersAgentViewProps {
  data: RaAgentData;
  /** Whether Reminders is on hold — owned above this component (§6). */
  onHold: boolean;
  onOpenSettings?: () => void;
  /** Confirmed hold, or resume. Resume never asks (§6 → amendment 24 §6). */
  onSetHold?: (onHold: boolean) => void;
  /** The report period (spec 41) — the selector sits first in the header. */
  period?: PeriodValue;
  onPeriodChange?: (value: PeriodValue) => void;
  onOpenTriage?: () => void;
  onViewAllLogs?: () => void;
}

const SEGMENT_COLORS = ['var(--ai-3)', 'var(--success)', 'var(--gold)', 'var(--terracotta)'];

const EVENT_TONE: Record<RaEventTone, string> = {
  held: 'var(--gold)',
  escalated: 'var(--terracotta)',
  agent: 'var(--ai-3)',
  outcome: 'var(--success)',
  batch: 'var(--ink-4)',
};

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

function SectionHeading({ n, title, sub }: { n: string; title: string; sub: string }) {
  return (
    <div className="mb-2.5 flex items-baseline gap-2.5">
      <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--ink-4)]">{n}</span>
      <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">{title}</h2>
      <span className="text-[12px] text-[var(--ink-4)]">{sub}</span>
    </div>
  );
}

function WeeklyChart({ weeks }: { weeks: RaWeek[] }) {
  const W = 720;
  const H = 150;
  const padB = 18;
  const maxVal = raWeeklyAxisMax(weeks);
  const bw = W / weeks.length;
  const barW = bw * 0.5;
  const y = (v: number) => H - padB - (v / maxVal) * (H - padB - 6);
  return (
    <svg
      viewBox={`0 0 ${String(W)} ${String(H)}`}
      className="w-full"
      role="img"
      aria-label={`Confirmations per week over ${String(weeks.length)} weeks`}
    >
      {[0, maxVal / 2, maxVal].map((t) => (
        <g key={t}>
          <line x1={0} x2={W} y1={y(t)} y2={y(t)} stroke="var(--cream-2)" strokeWidth={1} />
          <text x={2} y={y(t) - 2} fontSize={9} fill="var(--ink-4)" fontFamily="var(--font-mono)">
            {t}
          </text>
        </g>
      ))}
      {weeks.map((wk, i) => (
        <rect
          key={wk.label}
          x={i * bw + (bw - barW) / 2}
          y={y(wk.confirmed)}
          width={barW}
          height={H - padB - y(wk.confirmed)}
          rx={2}
          fill="color-mix(in srgb, var(--success) 55%, var(--cream-1))"
        />
      ))}
      {weeks.map((wk, i) =>
        i % 2 === 0 ? (
          <text
            key={`x-${wk.label}`}
            x={i * bw + bw / 2}
            y={H - 4}
            fontSize={9}
            textAnchor="middle"
            fill="var(--ink-4)"
            fontFamily="var(--font-mono)"
          >
            {wk.label}
          </text>
        ) : null,
      )}
    </svg>
  );
}

function StepRow({ step }: { step: RaStep }) {
  return (
    <div
      className={cn(
        'grid grid-cols-[16px_1.6fr_0.6fr_0.9fr] items-center gap-3 border-t border-[var(--cream-2)] px-1 py-2.5 text-[12.5px]',
        step.off && 'opacity-60',
      )}
    >
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: step.dot }} aria-hidden />
      <span className="min-w-0">
        <span className="font-semibold text-[var(--ink-0)]">{step.name}</span>
        <span className="ml-2 text-[11.5px] text-[var(--ink-4)]">
          {step.purpose} · {step.offset}
        </span>
      </span>
      <span className="font-[var(--font-mono)] text-[var(--ink-2)]">{step.sent}</span>
      <span className="text-right">
        <span className="font-[var(--font-mono)] text-[13px] font-bold text-[var(--ink-0)]">
          {step.kpiValue}
        </span>
        {step.kpiLabel ? (
          <span className="ml-1 text-[11px] text-[var(--ink-4)]">{step.kpiLabel}</span>
        ) : null}
      </span>
    </div>
  );
}

function ChannelRow({ ch }: { ch: RaChannel }) {
  const pct = ch.sent > 0 ? Math.round((ch.confirmed / ch.sent) * 100) : 0;
  return (
    <div className="border-t border-[var(--cream-2)] px-1 py-2.5">
      <div className="flex items-baseline justify-between text-[12.5px]">
        <span className="font-semibold text-[var(--ink-0)]">{ch.label}</span>
        <span className="text-[var(--ink-3)]">
          <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{pct}%</span> ·{' '}
          {ch.confirmed.toLocaleString()} of {ch.sent.toLocaleString()}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--cream-2)]">
        <div className="h-full rounded-full bg-[var(--success)]" style={{ width: `${String(pct)}%` }} />
      </div>
    </div>
  );
}

function ActivityRow({ e }: { e: RaEvent }) {
  return (
    <li className="flex items-baseline gap-2.5 py-1.5">
      <span
        className="mt-1 h-2 w-2 shrink-0 rounded-full"
        style={{ background: EVENT_TONE[e.tone] }}
        aria-hidden
      />
      <span className="flex-1 text-[12.5px] text-[var(--ink-1)]">{e.text}</span>
      <span className="shrink-0 font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">{e.at}</span>
    </li>
  );
}

export function RemindersAgentView({
  data,
  onOpenSettings,
  onHold,
  onSetHold,
  period,
  onPeriodChange,
  onOpenTriage,
  onViewAllLogs,
}: RemindersAgentViewProps) {
  const [asking, setAsking] = useState(false);
  const hero = data.hero;
  const totalSeg = hero.segments.reduce((s, x) => s + x.value, 0) || 1;

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          {/* Counted from the steps, and it carries the hold state (§6). */}
          <div className="pc-eyebrow text-[var(--ink-3)]">{raEyebrow(data, onHold)}</div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {data.titleLead} <span className="text-[var(--ai-3)]">{data.titleAccent}</span>
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--ink-3)]">{data.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {period && onPeriodChange ? (
            <PeriodSelector variant="agent" value={period} onChange={onPeriodChange} startedISO="2026-03-03" />
          ) : null}
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>
            Settings
          </Button>
          {/* Resume does not ask — nobody needs confirming that work should
              restart (§6 → amendment 24 §6). */}
          {onHold ? (
            <Button
              variant="primary"
              size="sm"
              iconLeft="play"
              onClick={() => {
                onSetHold?.(false);
              }}
            >
              Resume
            </Button>
          ) : (
            <Button
              variant="danger"
              size="sm"
              iconLeft="pause"
              onClick={() => {
                setAsking(true);
              }}
            >
              Hold this agent
            </Button>
          )}
        </div>
      </div>

      {/* waiting bar */}
      {data.waiting.count > 0 ? (
        <section
          aria-label="Waiting on you"
          className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--terracotta)_35%,transparent)] bg-[var(--terracotta-tint)] px-4 py-2.5"
        >
          <Icon kind="alert" size={15} stroke={2} style={{ color: 'var(--terracotta)' }} />
          <span className="text-[13px] font-semibold text-[var(--terracotta)]">
            {data.waiting.count} waiting on you
          </span>
          <span className="text-[12px] text-[var(--ink-2)]">{data.waiting.names}</span>
          <button
            type="button"
            className="ml-auto text-[12.5px] font-semibold text-[var(--terracotta)] hover:underline"
            onClick={onOpenTriage}
          >
            Open Triage →
          </button>
        </section>
      ) : null}

      {/* hero */}
      <div className="mt-4 grid gap-3 lg:grid-cols-[1.7fr_1fr]">
        <Card className="border-l-[3px] border-l-[var(--brand-indigo)]">
          <div className="pc-eyebrow text-[var(--ink-3)]">Confirmations · which step earned them</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-[var(--font-display)] text-[32px] font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
              {hero.confirmed.toLocaleString()}
            </span>
            <span className="text-[13px] text-[var(--ink-3)]">
              of {hero.sent.toLocaleString()} patients · {hero.caption}
            </span>
          </div>
          <div className="mt-3 flex h-3 overflow-hidden rounded-full">
            {hero.segments.map((seg, i) => (
              <div
                key={seg.label}
                style={{
                  width: `${String((seg.value / totalSeg) * 100)}%`,
                  background: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
                }}
                title={`${seg.label}: ${String(seg.value)}`}
              />
            ))}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5">
            {hero.segments.map((seg, i) => (
              <span key={seg.label} className="inline-flex items-center gap-1.5 text-[11.5px]">
                <span
                  className="h-2 w-2 rounded-[2px]"
                  style={{ background: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }}
                />
                <span className="text-[var(--ink-3)]">{seg.label}</span>
                <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{seg.value}</span>
              </span>
            ))}
          </div>
          {/* Divided, and by APPOINTMENTS. *Per 100 sent* counted a patient who
              got four messages four times (§6). */}
          <div className="mt-3 border-t border-[var(--cream-2)] pt-2 text-[12px] text-[var(--ink-3)]">
            Per 100 appointments →{' '}
            <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">
              {raPerHundredAppointments(data)}
            </span>{' '}
            confirmed
          </div>
        </Card>
        <div className="grid gap-3">
          {data.sideCards.map((c) => (
            <Card key={c.label}>
              <div className="pc-eyebrow text-[var(--ink-3)]">{c.label}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
                  {c.value}
                </span>
                {c.delta ? (
                  <span className="text-[11.5px] font-semibold text-[var(--success)]">{c.delta}</span>
                ) : null}
              </div>
              <div className="mt-0.5 text-[11.5px] text-[var(--ink-4)]">{c.caption}</div>
            </Card>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {data.kpis.map((k) => (
          <Card key={k.key}>
            <div className="pc-eyebrow text-[var(--ink-3)]">{k.label}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
                {k.value}
              </span>
              {k.delta ? (
                <span className="text-[11.5px] font-semibold text-[var(--success)]">{k.delta}</span>
              ) : null}
            </div>
            <div className="mt-0.5 text-[11.5px] leading-[1.45] text-[var(--ink-4)]">{k.caption}</div>
          </Card>
        ))}
      </div>

      {/* weekly performance */}
      {/* Bars only. The confirm-rate overlay was a second, differently-computed
          rate beside the hero's — which is how the page began arguing with
          itself (§6). */}
      <Card className="mt-3">
        <SectionHeading n="" title="Confirmed per week" sub="Last 13 weeks" />
        <WeeklyChart weeks={data.weeks} />
      </Card>

      {/* 01 step performance */}
      <Card className="mt-3">
        <SectionHeading n="01" title="Step performance" sub="Every step, and what it earns." />
        <div className="grid grid-cols-[16px_1.6fr_0.6fr_0.9fr] gap-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink-4)]">
          <span />
          <span>Step</span>
          <span>Sent</span>
          <span className="text-right">Primary KPI</span>
        </div>
        {data.steps.map((s) => (
          <StepRow key={s.key} step={s} />
        ))}
      </Card>

      {/* 02 channel breakdown */}
      <Card className="mt-3">
        <SectionHeading n="02" title="Channel breakdown" sub="Confirmations by delivery channel." />
        {data.channels.map((c) => (
          <ChannelRow key={c.label} ch={c} />
        ))}
        <p className="mt-2.5 flex items-start gap-1.5 rounded-[var(--r-md)] bg-[var(--cream-0)] px-3 py-2 text-[11.5px] text-[var(--ink-3)]">
          <Icon kind="lock" size={12} stroke={2} style={{ color: 'var(--ink-4)', marginTop: 1 }} />
          AI calls go only to patients who have not confirmed, and cannot read out pre-visit
          instructions.
        </p>
      </Card>

      {/* 03 live activity */}
      <Card className="mt-3">
        <div className="flex items-start justify-between">
          <SectionHeading n="03" title="Live activity" sub="Most recent events, including what it chose not to send." />
          <button
            type="button"
            className="text-[12px] font-semibold text-[var(--ai-3)] hover:underline"
            onClick={onViewAllLogs}
          >
            View all logs →
          </button>
        </div>
        <ul className="m-0 list-none p-0" aria-live="polite">
          {data.activity.map((e) => (
            <ActivityRow key={e.id} e={e} />
          ))}
        </ul>
      </Card>
      {asking ? (
        <HoldModal
          onCancel={() => {
            setAsking(false);
          }}
          onConfirm={() => {
            setAsking(false);
            onSetHold?.(true);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The hold confirmation (§6 → amendment 24 §6). An **in-page** modal, never
 * `window.confirm`: the browser dialog cannot say what holding actually does, and
 * what it does is the only reason to ask.
 */
function HoldModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.35)] px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hold-reminders-title"
    >
      <div className="w-full max-w-[420px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-3)]">
        <h2 id="hold-reminders-title" className="m-0 text-[16px] font-bold text-[var(--ink-0)]">
          Hold Appointment Reminders?
        </h2>
        <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">
          Nothing is sent until you resume — no confirmations, no post-op checks, no review asks. Replies are still
          answered, and calls already in Triage stay there.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>
            Hold
          </Button>
        </div>
      </div>
    </div>
  );
}
